import { getApiErrorMessage } from "@core/utility/get-error-message";
import {
  getActivityLogByPatientController,
  GetResponseActivityLogModel,
  QueryKeyActivityLog,
} from "@factory/model/query-model-activity-log";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { AxiosError } from "axios";
import { useQuery } from "react-query";

import { authHeaders } from "../authentication/auth-header";
import { useSessionContext } from "../authentication/session.context";

export function useQyGetActivityLogByPatient(
  patient: string,
  search: string = "",
  limit = 99,
  offset = 0,
  order?: object,
  filter?: Record<string, string>,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseActivityLogModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    patient: string,
    search: string | undefined = undefined,
    limit: number | undefined = undefined,
    offset: number | undefined = undefined,
    order: object | undefined = undefined,
    filter: Record<string, string> | undefined = undefined,
    dateFilter: string | undefined = undefined,
    startDate: string | undefined = undefined,
    endDate: string | undefined = undefined,
  ) => {
    await checkSession();

    showProgress();
    const operation = await getActivityLogByPatientController(
      patient,
      search,
      limit,
      offset,
      order,
      filter ? { filter: JSON.stringify(filter) } : undefined,
      dateFilter,
      startDate,
      endDate,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseActivityLogModel;
  };

  return useQuery({
    enabled: enabled && !!patient,
    queryKey: [
      QueryKeyActivityLog,
      "patient",
      patient,
      search,
      limit,
      offset,
      order,
      filter,
    ],
    queryFn: () => apiFn(patient, search, limit, offset, order, filter),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}
